import { useCallback, useEffect, useState } from "react";
import { getNotes, updateNotes, duplicateNote } from "../../../network/network.jsx";
import { getEntityShareLink } from "../../../other/utils/otherUtils.jsx";
import { copyTextToClipboard } from "../../../other/utils/utils.jsx";
import { DEEP_LINK_URL_KEYS } from "../../../other/constants/OtherKeys.jsx";
import { toast } from "sonner";
import { BoardNote } from "@/bryntum-lib/models/BoardNote.js";
import { useConfirm } from "@omit/react-confirm-dialog";
import { getSavedBoardSelection } from "../../hooks/useBoards";
import { getIsArchivedForBoard } from "../../../other/utils/otherUtils";
import { EntityRecord } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

export interface INoteApiPayload {
  content: string;
  board: number;
  archived: boolean;
}

type RecordId = BoardNote["id"];

export type TUseSwivuNotesReturn = ReturnType<typeof useSwivuNotes>;

export const useSwivuNotes = ({ entityRecord }: { entityRecord: EntityRecord }) => {
  const context = useSwivuBryntumContext();
  const { project, user } = context;
  const boardNotesStore = project.boardNotesStore;
  const [loading, setLoading] = useState<boolean>(false);
  const [newNote, setNewNote] = useState<BoardNote | null>(null);
  const [notes, setNotes] = useState<BoardNote[]>([]);
  const confirm = useConfirm();

  const canEditRecord = useCallback(
    (noteRecord: BoardNote): boolean => {
      return user.can(noteRecord.canEdit);
    },
    [user]
  );

  const saveNote = useCallback(async (noteRecord: BoardNote, data: INoteApiPayload): Promise<void> => {
    try {
      setLoading(true);
      if (noteRecord.isPhantom) {
        await new Promise((res: (value?: unknown) => void) => {
          boardNotesStore.on(
            {
              async idChange({ record }: { record: BoardNote }) {
                if (record.id === noteRecord.id) {
                  await updateNotes(noteRecord.id, data);
                  res();
                }
              },
              once: true
            },
            this
          );
          boardNotesStore.add(noteRecord);
        });
      } else {
        await updateNotes(noteRecord.id, data);
      }
      toast("Note saved successfully");
    } catch (error) {
      toast.error("Something went wrong! Unable to save note");
      console.error("Failed to save note:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateNote = useCallback((noteRecord: BoardNote, data: Partial<BoardNote>): void => {
    noteRecord.set(data);
  }, []);

  const deleteNote = useCallback(
    async (recordId: RecordId): Promise<boolean> => {
      const noteRecord = boardNotesStore.getById(recordId) as unknown as BoardNote;
      if (!noteRecord) return false;

      const confirmed = await confirm({
        title: "Delete note",
        description: `Are you sure you want to delete "${noteRecord.name || "this note"}"? This action cannot be undone.`,
        confirmButton: {
          variant: "destructive",
          title: "Delete note"
        },
        cancelButton: {
          variant: "ghost"
        }
      });

      if (!confirmed) return false;

      try {
        setLoading(true);
        boardNotesStore.remove(recordId);
        toast.success("Note successfully deleted");
        return true;
      } catch (error) {
        console.error("Failed to delete note:", error);
        toast.error("Something went wrong! Unable to delete note");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [confirm, boardNotesStore]
  );

  const duplicateNoteRecord = useCallback(
    async (noteRecord: BoardNote): Promise<void> => {
      try {
        setLoading(true);

        // Create a phantom record for the duplicate
        const notePhantomRecord = boardNotesStore.createRecord({
          [entityRecord.entityType]: entityRecord.id,
          name: `Copy of ${noteRecord.name}`,
          archived: noteRecord.archived
        }) as unknown as BoardNote;

        // Add the phantom record to the store
        boardNotesStore.add(notePhantomRecord);

        // Listen for the ID change to make the API call
        const detacher = boardNotesStore.on("idChange", async ({ record }: { record: BoardNote }) => {
          if (record.id === notePhantomRecord.id) {
            try {
              await duplicateNote(notePhantomRecord.id, {
                board: noteRecord.boardId,
                archived: noteRecord.archived,
                duplicate_id: noteRecord.id
              });
              toast.success("Note duplicated");
            } catch (error) {
              console.error("Failed to duplicate note:", error);
              toast.error("Something went wrong! Unable to duplicate note");
            } finally {
              detacher();
            }
          }
        });
      } catch (error) {
        console.error("Failed to create duplicate note:", error);
        toast.error("Something went wrong! Unable to duplicate note");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [boardNotesStore, entityRecord]
  );

  const createNewNote = useCallback((entityRecord: EntityRecord) => {
    const newNote = boardNotesStore.createRecord({
      [entityRecord.entityType]: entityRecord.id
    }) as unknown as BoardNote;
    setNewNote(newNote);
  }, [boardNotesStore]);

  const removeNewNote = useCallback((): void => {
    setNewNote(null);
  }, []);

  const getNoteShareLink = useCallback((recordId: RecordId, entityRecord: EntityRecord): string => {
    let deepLinkUrlKey: string;
    let deepLinkSlug = "note";

    if (entityRecord.isTask || entityRecord.isPlanTaskModel) {
      deepLinkSlug = "document";
      deepLinkUrlKey = DEEP_LINK_URL_KEYS.task;
    } else if (entityRecord.isResource) {
      deepLinkUrlKey = DEEP_LINK_URL_KEYS.resource;
    } else if (entityRecord.isClient) {
      deepLinkUrlKey = DEEP_LINK_URL_KEYS.client;
    } else {
      return "";
    }

    const shareLinkData = getEntityShareLink({
      record: entityRecord,
      context,
      editorTabs: deepLinkSlug,
      entityType: deepLinkUrlKey,
      otherKeys: { [DEEP_LINK_URL_KEYS.note]: recordId },
      entityDetail: { name: `Note for ${entityRecord.name}` }
    });

    return shareLinkData.linkOnly;
  }, [context]);

  const copyNoteLink = useCallback(
    (noteRecord: BoardNote, entityRecord: EntityRecord): void => {
      const linkOnly = getNoteShareLink(noteRecord.id, entityRecord);
      if (linkOnly) {
        copyTextToClipboard(linkOnly, `Link to ${noteRecord.name || "note"} copied to clipboard successfully!`);
      } else {
        toast.error("Unable to generate link for this note");
      }
    },
    [getNoteShareLink]
  );

  const getNoteData = useCallback(async (recordId: RecordId): Promise<string | null> => {
    try {
      setLoading(true);
      const controller = new AbortController();
      const { data } = await getNotes(recordId, controller);
      return data?.content || null;
    } catch (error) {
      if ((error as Error)?.message === "canceled") return null;
      console.error("Failed to load note:", error);
      toast.error("Something went wrong! Unable to retrieve note");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshNotes = useCallback(() => {
    const selectedBoard = getSavedBoardSelection();
    const canShowArchivedRecords = getIsArchivedForBoard(selectedBoard.slug);

    const notes = boardNotesStore.query((record) => {
      return record[entityRecord.entityType] === entityRecord.id && !record.isPhantom && (canShowArchivedRecords || !record.archived);
    }, true) as unknown as BoardNote[];

    const sortedNotes = notes.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    setNotes(sortedNotes);
  }, [boardNotesStore, entityRecord]);

  useEffect(() => {
    refreshNotes();
  }, [refreshNotes, entityRecord]);

  return {
    saveNote,
    deleteNote,
    duplicateNoteRecord,
    createNewNote,
    newNote,
    removeNewNote,
    getNoteShareLink,
    copyNoteLink,
    loading,
    getNoteData,
    refreshNotes,
    notes,
    updateNote,
    boardNotesStore,
    canEditRecord
  };
};
